import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  CreditCard, 
  CheckCircle, 
  Clock, 
  Calendar, 
  Loader, 
  AlertCircle, 
  Infinity, 
  ShieldCheck, 
  MessageCircle, 
  Users, 
  Zap, 
  Heart 
} from 'lucide-react';
import { STRIPE_PRODUCTS } from '../stripe-config';
import { redirectToCheckout, getUserSubscription, getUserOrders, formatCurrency, getSubscriptionStatus, isUserPremium } from '../lib/stripe';
import { supabase } from '../lib/supabase';
import { useUser } from '../contexts/UserContext';
import BackButton from '../components/BackButton';

const Premium = () => {
  const navigate = useNavigate();
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [purchaseLoading, setPurchaseLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<any>(null);
  const [orders, setOrders] = useState<any[]>([]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    
    fetchUserData();
  }, [user, navigate]);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch subscription data
      const subscriptionData = await getUserSubscription();
      setSubscription(subscriptionData);
      
      // Fetch orders data
      const ordersData = await getUserOrders();
      setOrders(ordersData);
    } catch (err) {
      console.error('Error fetching user data:', err);
      setError('Failed to load subscription data. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handlePurchase = async (priceId: string, mode: 'payment' | 'subscription') => {
    try {
      setPurchaseLoading(priceId);
      setError(null);
      
      await redirectToCheckout(priceId, mode);
    } catch (err) {
      console.error('Error during checkout:', err);
      setError('Failed to initiate checkout. Please try again later.');
      setPurchaseLoading(null);
    }
  };

  const formatDate = (timestamp: number) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp * 1000).toLocaleDateString('de-DE');
  };

  const isPremium = isUserPremium(subscription, orders);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-12 w-12 animate-spin text-orange-500 mx-auto mb-4" />
          <p className="text-gray-600">Lade Premium-Informationen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <BackButton />
      
      {/* Hero Section */}
      <div className="relative mb-12 rounded-2xl overflow-hidden">
        <div 
          className="h-64 bg-cover bg-center"
          style={{
            backgroundImage: "url('https://images.unsplash.com/photo-1414235077428-338989a2e8c0?q=80&w=2000&auto=format&fit=crop')"
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center text-white">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 transform transition-all duration-500 hover:scale-105">
                Meet&Eat Premium
              </h1>
              <p className="text-xl text-gray-200 max-w-2xl mx-auto">
                Entdecke alle Vorteile und genieße das volle Erlebnis
              </p>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-8 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded flex items-center">
          <AlertCircle className="h-5 w-5 mr-2" />
          <span>{error}</span>
        </div>
      )}

      {/* Subscription Status */}
      {isPremium && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-8">
          <div className="flex items-center space-x-3">
            <div className="bg-green-100 p-3 rounded-full">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-green-800">Premium-Status aktiv</h2>
              <p className="text-green-700">
                {subscription?.subscription_status === 'active' ? (
                  <>Dein Premium-Abonnement ist aktiv bis {formatDate(subscription.current_period_end)}.</>
                ) : (
                  <>Du hast Premium auf Lebenszeit.</>
                )}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Premium Features */}
      <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
        <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Premium-Vorteile</h2>
        
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-orange-50 rounded-xl p-6 transform transition-all duration-300 hover:scale-105">
            <div className="flex items-center space-x-3 mb-4">
              <div className="bg-orange-100 p-3 rounded-full">
                <Users className="h-6 w-6 text-orange-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Unbegrenzte Kontakte</h3>
            </div>
            <p className="text-gray-600">Verbinde dich mit so vielen Essenspartnern wie du möchtest, ohne Limits.</p>
          </div>
          
          <div className="bg-blue-50 rounded-xl p-6 transform transition-all duration-300 hover:scale-105">
            <div className="flex items-center space-x-3 mb-4">
              <div className="bg-blue-100 p-3 rounded-full">
                <MessageCircle className="h-6 w-6 text-blue-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Unbegrenzte Nachrichten</h3>
            </div>
            <p className="text-gray-600">Kommuniziere so viel du willst mit deinen Matches und Essenspartnern.</p>
          </div>
          
          <div className="bg-purple-50 rounded-xl p-6 transform transition-all duration-300 hover:scale-105">
            <div className="flex items-center space-x-3 mb-4">
              <div className="bg-purple-100 p-3 rounded-full">
                <Heart className="h-6 w-6 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Erweiterte Matches</h3>
            </div>
            <p className="text-gray-600">Erhalte Zugang zu erweiterten Matching-Algorithmen für bessere Essenspartner.</p>
          </div>
          
          <div className="bg-green-50 rounded-xl p-6 transform transition-all duration-300 hover:scale-105">
            <div className="flex items-center space-x-3 mb-4">
              <div className="bg-green-100 p-3 rounded-full">
                <Zap className="h-6 w-6 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Prioritäts-Support</h3>
            </div>
            <p className="text-gray-600">Genieße bevorzugten Kundensupport und schnellere Antwortzeiten.</p>
          </div>
          
          <div className="bg-yellow-50 rounded-xl p-6 transform transition-all duration-300 hover:scale-105">
            <div className="flex items-center space-x-3 mb-4">
              <div className="bg-yellow-100 p-3 rounded-full">
                <ShieldCheck className="h-6 w-6 text-yellow-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Verifiziertes Profil</h3>
            </div>
            <p className="text-gray-600">Erhalte ein verifiziertes Profilabzeichen für mehr Vertrauen und Sichtbarkeit.</p>
          </div>
          
          <div className="bg-red-50 rounded-xl p-6 transform transition-all duration-300 hover:scale-105">
            <div className="flex items-center space-x-3 mb-4">
              <div className="bg-red-100 p-3 rounded-full">
                <Infinity className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Keine Werbung</h3>
            </div>
            <p className="text-gray-600">Genieße eine werbefreie Erlebnis auf der gesamten Plattform.</p>
          </div>
        </div>
      </div>

      {/* Pricing Plans */}
      <div className="grid md:grid-cols-2 gap-8 mb-12">
        {STRIPE_PRODUCTS.map((product) => (
          <div 
            key={product.id}
            className="bg-white rounded-xl shadow-lg overflow-hidden transform transition-all duration-300 hover:shadow-xl"
          >
            <div className={`p-6 ${product.mode === 'payment' ? 'bg-gradient-to-r from-purple-500 to-purple-600' : 'bg-gradient-to-r from-orange-500 to-orange-600'} text-white`}>
              <h3 className="text-2xl font-bold mb-2">{product.name}</h3>
              <div className="flex items-center space-x-2">
                {product.mode === 'payment' ? (
                  <Infinity className="h-5 w-5" />
                ) : (
                  <Calendar className="h-5 w-5" />
                )}
                <span>
                  {product.mode === 'payment' ? 'Einmalzahlung' : 'Monatliches Abonnement'}
                </span>
              </div>
            </div>
            
            <div className="p-6">
              <p className="text-gray-600 mb-6">{product.description}</p>
              
              <div className="mb-6">
                <div className="text-3xl font-bold text-gray-900 mb-1">
                  {formatCurrency(product.price, product.currency)}
                </div>
                <div className="text-gray-500 text-sm">
                  {product.mode === 'payment' ? 'Einmalig' : 'Pro Monat'}
                </div>
              </div>
              
              <button
                onClick={() => handlePurchase(product.priceId, product.mode)}
                disabled={purchaseLoading === product.priceId || isPremium}
                className={`w-full py-3 px-4 rounded-lg font-medium flex items-center justify-center space-x-2 ${
                  isPremium
                    ? 'bg-gray-300 cursor-not-allowed text-gray-500'
                    : purchaseLoading === product.priceId
                    ? 'bg-gray-400 cursor-wait text-white'
                    : 'bg-orange-500 hover:bg-orange-600 text-white transition'
                }`}
              >
                {purchaseLoading === product.priceId ? (
                  <>
                    <Loader className="h-5 w-5 animate-spin" />
                    <span>Wird verarbeitet...</span>
                  </>
                ) : isPremium ? (
                  <>
                    <CheckCircle className="h-5 w-5" />
                    <span>Bereits Premium</span>
                  </>
                ) : (
                  <>
                    <CreditCard className="h-5 w-5" />
                    <span>Jetzt kaufen</span>
                  </>
                )}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Subscription Details (if active) */}
      {subscription && subscription.subscription_status !== 'not_started' && (
        <div className="bg-white rounded-xl shadow-lg p-8 mb-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Abonnement-Details</h2>
          
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center space-x-3 mb-2">
                <Clock className="h-5 w-5 text-gray-500" />
                <h3 className="font-medium text-gray-700">Status</h3>
              </div>
              <p className={`text-lg font-semibold ${
                subscription.subscription_status === 'active' ? 'text-green-600' : 
                subscription.subscription_status === 'canceled' ? 'text-red-600' : 
                'text-yellow-600'
              }`}>
                {subscription.subscription_status === 'active' ? 'Aktiv' : 
                 subscription.subscription_status === 'canceled' ? 'Gekündigt' : 
                 subscription.subscription_status === 'trialing' ? 'Testphase' : 
                 subscription.subscription_status}
              </p>
            </div>
            
            <div className="bg-gray-50 p-4 rounded-lg">
              <div className="flex items-center space-x-3 mb-2">
                <Calendar className="h-5 w-5 text-gray-500" />
                <h3 className="font-medium text-gray-700">Aktueller Zeitraum</h3>
              </div>
              <p className="text-lg font-semibold text-gray-900">
                {formatDate(subscription.current_period_start)} - {formatDate(subscription.current_period_end)}
              </p>
            </div>
            
            {subscription.payment_method_brand && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center space-x-3 mb-2">
                  <CreditCard className="h-5 w-5 text-gray-500" />
                  <h3 className="font-medium text-gray-700">Zahlungsmethode</h3>
                </div>
                <p className="text-lg font-semibold text-gray-900">
                  {subscription.payment_method_brand.charAt(0).toUpperCase() + subscription.payment_method_brand.slice(1)} •••• {subscription.payment_method_last4}
                </p>
              </div>
            )}
            
            {subscription.cancel_at_period_end && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center space-x-3 mb-2">
                  <AlertCircle className="h-5 w-5 text-yellow-500" />
                  <h3 className="font-medium text-gray-700">Kündigungsinfo</h3>
                </div>
                <p className="text-gray-600">
                  Dein Abonnement wurde gekündigt und endet am {formatDate(subscription.current_period_end)}.
                </p>
              </div>
            )}
          </div>
          
          {subscription.subscription_status === 'active' && !subscription.cancel_at_period_end && (
            <div className="mt-6 text-center">
              <button
                onClick={() => {
                  // This would typically open a modal or redirect to a cancellation page
                  alert('Diese Funktion ist noch nicht implementiert. Bitte kontaktiere den Support, um dein Abonnement zu kündigen.');
                }}
                className="text-red-600 hover:text-red-800 font-medium"
              >
                Abonnement kündigen
              </button>
            </div>
          )}
        </div>
      )}

      {/* Order History */}
      {orders.length > 0 && (
        <div className="bg-white rounded-xl shadow-lg p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Bestellverlauf</h2>
          
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Datum
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Betrag
                  </th>
                  <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {orders.map((order) => (
                  <tr key={order.order_id}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {new Date(order.order_date).toLocaleDateString('de-DE')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatCurrency(order.amount_total, order.currency)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                        order.order_status === 'completed' ? 'bg-green-100 text-green-800' : 
                        order.order_status === 'canceled' ? 'bg-red-100 text-red-800' : 
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {order.order_status === 'completed' ? 'Abgeschlossen' : 
                         order.order_status === 'canceled' ? 'Storniert' : 
                         'Ausstehend'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Premium;